import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import {
    IChartApi,
    ISeriesApi,
    ISeriesPrimitive,
    ISeriesPrimitivePaneRenderer,
    ISeriesPrimitivePaneView,
    LastPriceAnimationMode,
    SeriesAttachedParameter,
    SeriesPrimitivePaneViewZOrder,
    SeriesType,
    Time,
} from 'lightweight-charts'
import tinycolor from 'tinycolor2'
import Chart from '../../chart'
import { BaselineSeriesOptions } from './options'
import { BaselineData } from '../../chart.typings'

const ANIMATION_PERIOD_MS = 2600

/** Mirrors `animationStagesData` / `animationData` from lightweight-charts' last-price animation. */
const ANIMATION_STAGES: ReadonlyArray<{
    start: number
    end: number
    startRadius: number
    endRadius: number
    startFillAlpha: number
    endFillAlpha: number
    startStrokeAlpha: number
    endStrokeAlpha: number
}> = [
    {
        start: 0,
        end: 0.25,
        startRadius: 4,
        endRadius: 10,
        startFillAlpha: 0.25,
        endFillAlpha: 0,
        startStrokeAlpha: 0.4,
        endStrokeAlpha: 0.8,
    },
    {
        start: 0.25,
        end: 0.25 + 0.275,
        startRadius: 10,
        endRadius: 14,
        startFillAlpha: 0,
        endFillAlpha: 0,
        startStrokeAlpha: 0.8,
        endStrokeAlpha: 0,
    },
    {
        start: 0.25 + 0.275,
        end: 1,
        startRadius: 14,
        endRadius: 14,
        startFillAlpha: 0,
        endFillAlpha: 0,
        startStrokeAlpha: 0,
        endStrokeAlpha: 0,
    },
]

function animationStageColor(lineColor: string, stage: number, startAlpha: number, endAlpha: number): string {
    const alpha = startAlpha + (endAlpha - startAlpha) * stage
    return tinycolor(lineColor).setAlpha(alpha).toRgbString()
}

function animationStageRadius(stage: number, startRadius: number, endRadius: number): number {
    return startRadius + (endRadius - startRadius) * stage
}

function pulseVisuals(durationSinceStart: number, lineColor: string) {
    const globalStage = ((durationSinceStart % ANIMATION_PERIOD_MS) / ANIMATION_PERIOD_MS)
    const current = ANIMATION_STAGES.find((s) => globalStage >= s.start && globalStage <= s.end)
    if (!current) {
        return {
            fillColor: tinycolor(lineColor).setAlpha(0).toRgbString(),
            strokeColor: tinycolor(lineColor).setAlpha(0).toRgbString(),
            radius: 14,
        }
    }
    const sub =
        (globalStage - current.start) / (current.end - current.start || 1)
    return {
        fillColor: animationStageColor(lineColor, sub, current.startFillAlpha, current.endFillAlpha),
        strokeColor: animationStageColor(lineColor, sub, current.startStrokeAlpha, current.endStrokeAlpha),
        radius: animationStageRadius(sub, current.startRadius, current.endRadius),
    }
}

function baselineMidPointPrice(
    chartView: Chart,
    options: BaselineSeriesOptions,
    paneIndex: number
): number | null {
    const paneId = chartView.getPaneIdByIndex(paneIndex)
    const el = paneId ? chartView.getPaneElementById(paneId) : undefined
    const mediaHeightCss = el?.clientHeight ?? 0
    if (mediaHeightCss <= 0) return null
    const srs = chartView.getBlankSeries()
    if (!srs) return null
    const startPrice = srs.coordinateToPrice(0)
    const endPrice = srs.coordinateToPrice(mediaHeightCss)
    if (startPrice === null || endPrice === null) return null
    const low = Math.min(Number(startPrice), Number(endPrice))
    const high = Math.max(Number(startPrice), Number(endPrice))
    if (chartView.screenerChart && options.baseValue >= 1) {
        return options.baseValue
    }
    return low + (high - low) * (options.baseValue / 100)
}

function lastLineColor(midPoint: number, value: number, options: BaselineSeriesOptions): string {
    return value > midPoint ? options.topLineColor : options.bottomLineColor
}

type PulseRenderData = {
    centerX: number
    centerY: number
    seriesLineColor: string
    seriesLineWidth: number
    fillColor: string
    strokeColor: string
    radius: number
}

class BaselineLastPricePulseRenderer implements ISeriesPrimitivePaneRenderer {
    private _data: PulseRenderData | null = null

    setData(data: PulseRenderData | null) {
        this._data = data
    }

    draw(target: CanvasRenderingTarget2D) {
        target.useBitmapCoordinateSpace((scope) => this.drawImpl(scope))
    }

    private drawImpl(scope: BitmapCoordinatesRenderingScope) {
        const data = this._data
        if (data === null) return
        const ctx = scope.context
        const { horizontalPixelRatio, verticalPixelRatio } = scope
        const tickWidth = Math.max(1, Math.floor(horizontalPixelRatio))
        const correction = (tickWidth % 2) / 2
        const centerX = Math.round(data.centerX * horizontalPixelRatio) + correction
        const centerY = data.centerY * verticalPixelRatio

        ctx.fillStyle = data.seriesLineColor
        ctx.beginPath()
        const centerPointRadius = Math.max(2, data.seriesLineWidth * 1.5) * horizontalPixelRatio
        ctx.arc(centerX, centerY, centerPointRadius, 0, 2 * Math.PI, false)
        ctx.fill()

        ctx.fillStyle = data.fillColor
        ctx.beginPath()
        ctx.arc(centerX, centerY, data.radius * horizontalPixelRatio, 0, 2 * Math.PI, false)
        ctx.fill()

        ctx.lineWidth = tickWidth
        ctx.strokeStyle = data.strokeColor
        ctx.beginPath()
        ctx.arc(centerX, centerY, data.radius * horizontalPixelRatio + tickWidth / 2, 0, 2 * Math.PI, false)
        ctx.stroke()
    }
}

class BaselineLastPricePulsePaneView implements ISeriesPrimitivePaneView {
    private readonly _primitive: BaselineLastPricePulsePrimitive
    private readonly _renderer: BaselineLastPricePulseRenderer

    constructor(primitive: BaselineLastPricePulsePrimitive) {
        this._primitive = primitive
        this._renderer = new BaselineLastPricePulseRenderer()
    }

    zOrder(): SeriesPrimitivePaneViewZOrder {
        return 'top'
    }

    renderer(): ISeriesPrimitivePaneRenderer {
        this._primitive.updateRendererData(this._renderer)
        return this._renderer
    }
}

/**
 * Custom baseline uses `addCustomSeries`; lightweight-charts does not attach {@link SeriesLastPriceAnimationPaneView}
 * for `Custom` series. This primitive reproduces the same pulse when {@link LastPriceAnimationMode} is enabled on the series.
 */
export class BaselineLastPricePulsePrimitive implements ISeriesPrimitive<Time> {
    private readonly _chartView: Chart
    private readonly _paneIndex: number
    private _chart: IChartApi | null = null
    private _series: ISeriesApi<'Custom', Time, BaselineData> | null = null
    private _requestUpdate: (() => void) | null = null
    private _unsubscribeData: (() => void) | null = null

    private _paneViews: BaselineLastPricePulsePaneView[] = [new BaselineLastPricePulsePaneView(this)]

    private _startTime = performance.now()
    private _endTime = this._startTime - 1
    private _rafId: number | null = null

    constructor(chartView: Chart, paneIndex: number) {
        this._chartView = chartView
        this._paneIndex = paneIndex
    }

    paneViews(): readonly ISeriesPrimitivePaneView[] {
        return this._paneViews
    }

    attached(param: SeriesAttachedParameter<Time, SeriesType>): void {
        this._chart = param.chart
        this._series = param.series as ISeriesApi<'Custom', Time, BaselineData>
        this._requestUpdate = param.requestUpdate
        this._unsubscribeData = param.series.subscribeDataChanged((scope) => {
            if (scope === 'full') {
                this._endTime = this._startTime - 1
            } else {
                this.onNewRealtimeData()
            }
            this._requestUpdate?.()
        })
    }

    detached(): void {
        this._unsubscribeData?.()
        this._unsubscribeData = null
        this._chart = null
        this._series = null
        this._requestUpdate = null
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId)
            this._rafId = null
        }
    }

    updateAllViews(): void {
        if (this.animationModeVisible() && this.animationActive()) {
            this.scheduleRaf()
        }
    }

    private scheduleRaf(): void {
        if (this._rafId !== null || !this._requestUpdate) return
        this._rafId = requestAnimationFrame(() => {
            this._rafId = null
            if (!this.animationModeVisible()) return
            if (this.animationActive()) {
                this._requestUpdate()
                this.scheduleRaf()
            }
        })
    }

    private animationModeVisible(): boolean {
        const mode = this._series?.options().lastPriceAnimation
        return mode !== undefined && mode !== LastPriceAnimationMode.Disabled
    }

    private animationActive(): boolean {
        const mode = this._series?.options().lastPriceAnimation
        if (mode === undefined || mode === LastPriceAnimationMode.Disabled) return false
        if (mode === LastPriceAnimationMode.Continuous) return true
        return performance.now() <= this._endTime
    }

    private onNewRealtimeData(): void {
        if (this._series?.options().lastPriceAnimation !== LastPriceAnimationMode.OnDataUpdate) return
        const now = performance.now()
        const timeToAnimationEnd = this._endTime - now
        if (timeToAnimationEnd > 0) {
            if (timeToAnimationEnd < ANIMATION_PERIOD_MS / 4) {
                this._endTime += ANIMATION_PERIOD_MS
            }
            return
        }
        this._startTime = now
        this._endTime = now + ANIMATION_PERIOD_MS
    }

    private durationMs(): number {
        return this.animationActive() ? performance.now() - this._startTime : ANIMATION_PERIOD_MS - 1
    }

    updateRendererData(renderer: BaselineLastPricePulseRenderer): void {
        renderer.setData(null)
        if (!this._chart || !this._series || !this.animationModeVisible()) return

        const seriesData = this._series.data()
        const lastBar = seriesData[seriesData.length - 1] as BaselineData | undefined
        if (!lastBar || lastBar.value === undefined) return

        const ts = this._chart.timeScale()
        const xCoord = ts.timeToCoordinate(lastBar.time as Time)
        if (xCoord === null) return

        const lastLogical = ts.coordinateToLogical(xCoord)
        const visible = ts.getVisibleLogicalRange()
        if (lastLogical === null || visible === null || lastLogical < visible.from || lastLogical > visible.to) return

        const opt = this._series.options() as BaselineSeriesOptions
        const mid = baselineMidPointPrice(this._chartView, opt, this._paneIndex)
        if (mid === null) return

        const yCoord = this._series.priceToCoordinate(lastBar.value)
        if (yCoord === null) return

        const lineColor = lastLineColor(mid, lastBar.value, opt)
        const lineWidth =
            typeof opt.lineWidth === 'number' && opt.lineWidth > 0 ? opt.lineWidth : 2
        const { fillColor, strokeColor, radius } = pulseVisuals(this.durationMs(), lineColor)

        renderer.setData({
            centerX: xCoord,
            centerY: yCoord,
            seriesLineColor: lineColor,
            seriesLineWidth: lineWidth,
            fillColor,
            strokeColor,
            radius,
        })
    }
}
